import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Eye, History, RotateCcw, Save } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { ApiError } from "@/api/client";
import {
  getPromptSettings,
  listPromptVersions,
  resetPromptSettings,
  restorePromptVersion,
  updatePromptSettings,
  type PromptSettings,
  type PromptVersion,
} from "@/api/settings";
import { Spinner } from "@/components/Spinner";
import { formatDateTime } from "@/lib/formatDate";
import { AdminHeader } from "@/pages/admin/components/AdminHeader";

type FormState = { persona: string; rules: string; edit_rules: string };

const EMPTY: FormState = { persona: "", rules: "", edit_rules: "" };

const fromSettings = (s: PromptSettings | PromptVersion): FormState => ({
  persona: s.persona,
  rules: s.rules,
  edit_rules: s.edit_rules,
});

export function PromptSettingsPage() {
  const qc = useQueryClient();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [note, setNote] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const settings = useQuery({ queryKey: ["prompt-settings"], queryFn: getPromptSettings });
  const versions = useQuery({
    queryKey: ["prompt-versions"],
    queryFn: () => listPromptVersions(),
    enabled: showHistory,
  });

  // Load the live prompt into the editor, and re-sync after every save /
  // reset / restore (the mutations below push their result into the cache).
  useEffect(() => {
    if (settings.data) setForm(fromSettings(settings.data));
  }, [settings.data]);

  const applySaved = (saved: PromptSettings, message: string) => {
    qc.setQueryData(["prompt-settings"], saved);
    void qc.invalidateQueries({ queryKey: ["prompt-versions"] });
    setNote("");
    toast.success(message);
  };

  const save = useMutation({
    mutationFn: () => updatePromptSettings({ ...form, note: note.trim() || null }),
    onSuccess: (saved) => applySaved(saved, "Prompt saved. New drafts use it immediately."),
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Save failed."),
  });

  const reset = useMutation({
    mutationFn: resetPromptSettings,
    onSuccess: (saved) => applySaved(saved, "Prompt reset to the shipped default."),
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Reset failed."),
  });

  const restore = useMutation({
    mutationFn: (versionId: number) => restorePromptVersion(versionId),
    onSuccess: (saved) => applySaved(saved, "Version restored as the live prompt."),
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Restore failed."),
  });

  const busy = save.isPending || reset.isPending || restore.isPending;
  const live = settings.data;
  const dirty = !!live && JSON.stringify(form) !== JSON.stringify(fromSettings(live));
  const token = live?.max_chars_token ?? "{{max_chars}}";

  if (settings.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner label="Loading prompt…" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <AdminHeader title="AI prompt" backTo="/admin" backAs="home" />
      <main className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex-1 flex flex-col gap-4">
        <div className="alert-info text-xs">
          These sections are sent to Gemini at the top of every draft. Edits apply to the next draft
          generated – no restart or redeploy. The response format (greeting, one answer,
          "Thank you."), the {live?.max_chars ?? 500}-character limit, the current-date block and the
          retrieved context are fixed in code and are not editable here. Use{" "}
          <code>{token}</code> anywhere you need the character limit.
          {live?.is_default && (
            <> Nothing has been saved yet, so the shipped default is currently in force.</>
          )}
        </div>

        <form
          className="card p-5 space-y-5"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <Section
            label="Persona"
            hint="Opens the prompt: who the model is and what it is being asked to do."
            value={form.persona}
            rows={4}
            onChange={(v) => setForm((f) => ({ ...f, persona: v }))}
          />
          <Section
            label="Rules"
            hint="One rule per line, each starting with a dash. Rendered after the response-format block."
            value={form.rules}
            rows={14}
            onChange={(v) => setForm((f) => ({ ...f, rules: v }))}
          />
          <Section
            label="Application-edit rules"
            hint="Added only when the govt DB returned an application status – these rules quote that status."
            value={form.edit_rules}
            rows={10}
            onChange={(v) => setForm((f) => ({ ...f, edit_rules: v }))}
          />

          <div className="flex flex-col sm:flex-row sm:items-end gap-3">
            <label className="flex-1 block">
              <span className="text-sm font-semibold">Note for the history (optional)</span>
              <input
                className="input-field mt-1"
                value={note}
                maxLength={200}
                placeholder="e.g. softened the tone on edit requests"
                onChange={(e) => setNote(e.target.value)}
              />
            </label>
            <div className="flex gap-2">
              <button type="submit" className="btn-primary" disabled={!dirty || busy}>
                <Save size={14} /> {save.isPending ? "Saving…" : "Save"}
              </button>
              <button
                type="button"
                className="btn-secondary"
                disabled={busy}
                onClick={() => {
                  if (
                    window.confirm(
                      "Replace the live prompt with the shipped default? The current version stays in the history and can be restored.",
                    )
                  ) {
                    reset.mutate();
                  }
                }}
              >
                <RotateCcw size={14} /> Reset to default
              </button>
            </div>
          </div>
          {dirty && (
            <p className="text-xs text-amber-700 dark:text-amber-300">
              Unsaved changes – drafts still use the last saved version.
            </p>
          )}
        </form>

        <div className="card p-4">
          <button
            type="button"
            className="w-full flex items-center justify-between text-sm font-semibold"
            onClick={() => setShowPreview((v) => !v)}
          >
            <span className="inline-flex items-center gap-2">
              <Eye size={14} /> Full prompt sent to Gemini
            </span>
            <span className="text-xs font-normal text-slate-500 dark:text-slate-400">
              {showPreview ? "Hide" : "Show"}
            </span>
          </button>
          {showPreview && (
            <>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                The last saved version, rendered with a sample ticket. Save to refresh it.
              </p>
              <pre className="mt-2 max-h-[28rem] overflow-auto rounded-md bg-canvas-subtle dark:bg-surface p-3 text-[11px] leading-relaxed whitespace-pre-wrap font-mono">
                {live?.preview}
              </pre>
            </>
          )}
        </div>

        <div className="card p-4">
          <button
            type="button"
            className="w-full flex items-center justify-between text-sm font-semibold"
            onClick={() => setShowHistory((v) => !v)}
          >
            <span className="inline-flex items-center gap-2">
              <History size={14} /> Version history
            </span>
            <span className="text-xs font-normal text-slate-500 dark:text-slate-400">
              {showHistory ? "Hide" : "Show"}
            </span>
          </button>
          {showHistory && (
            <div className="mt-3">
              {versions.isLoading ? (
                <Spinner label="Loading history…" />
              ) : versions.data && versions.data.items.length > 0 ? (
                <ul className="divide-y divide-canvas-border dark:divide-surface-border">
                  {versions.data.items.map((v, idx) => (
                    <li key={v.id} className="py-2 flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-xs font-medium">
                          {formatDateTime(v.created_at)}
                          {" · "}
                          {v.created_by_username ?? "unknown"}
                          {idx === 0 && (
                            <span className="ml-2 text-[10px] uppercase tracking-wide text-emerald-700 dark:text-emerald-300">
                              live
                            </span>
                          )}
                        </p>
                        {v.note && (
                          <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                            {v.note}
                          </p>
                        )}
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <button
                          type="button"
                          className="btn-secondary text-xs px-2 py-1"
                          onClick={() => setForm(fromSettings(v))}
                          title="Load this version into the editor without saving"
                        >
                          Load
                        </button>
                        <button
                          type="button"
                          className="btn-secondary text-xs px-2 py-1"
                          disabled={busy || idx === 0}
                          onClick={() => {
                            if (window.confirm("Make this version the live prompt?")) {
                              restore.mutate(v.id);
                            }
                          }}
                        >
                          Restore
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  No edits yet – the shipped default is in force.
                </p>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function Section({
  label,
  hint,
  value,
  rows,
  onChange,
}: {
  label: string;
  hint: string;
  value: string;
  rows: number;
  onChange: (v: string) => void;
}) {
  return (
    <label className="block">
      <span className="text-sm font-semibold">{label}</span>
      <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">{hint}</p>
      <textarea
        className="input-field font-mono text-xs leading-relaxed"
        rows={rows}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required
      />
      <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">
        {value.length.toLocaleString()} characters
      </p>
    </label>
  );
}
